import { useState } from "react";
import { useAccountContext } from "./useAccountContext";
import { useAuthContext } from "./useAuthContext";
import { useSettingsContext } from "./useSettingsContext";
import axios from 'axios';

export const useTraining = () => {
    const { username, user, clientApi, dispatch: authDispatch } = useAuthContext();
    const { account } = useAccountContext();
    const { isWinAuth } = useSettingsContext();

    const axiosInstance = axios.create({
        baseURL: `${clientApi}`,
        headers: {
            'accepts': 'application/json',
            'Content-Type': 'application/json',
        },
    });

    axiosInstance.interceptors.request.use(request => {
        // console.log('Set Bearer Token')
        const accessToken = user.token;
        if (accessToken) {
            request.headers['Authorization'] = `Bearer ${accessToken}`;
        }
        return request;
    }, error => {
        return Promise.reject(error);
    });

    axiosInstance.interceptors.response.use(
        response => response, // Directly return successful responses.
        async error => {
            const originalRequest = error.config;
            if (error.response.status === 401 && !originalRequest._retry) {
                originalRequest._retry = true; // Mark the request as retried to avoid infinite loops.
                try {
                    if (!isWinAuth) {
                        // Make a request to your auth server to refresh the token.
                        const response = await axiosInstance.post(`${clientApi}api/auth/refresh`, user);
                        // Store the new access and refresh tokens.
                        authDispatch({ type: 'Refresh', payload: response.data, clientApi: clientApi, username: username });

                        // Update the authorization header with the new access token.
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;
                    }
                    else {
                        const newToken = await acquireSilentToken();
                        let tmpUser = user;
                        tmpUser.token = newToken;
                        authDispatch({ type: 'REFRESH', payload: tmpUser, clientApi: clientApi, username: username });
                        axiosInstance.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
                    }
                    return axios(originalRequest); // Retry the original request with the new access token.
                } catch (refreshError) {
                    // Handle refresh token errors by clearing stored tokens and redirecting to the login page.
                    console.error('Token refresh failed:', refreshError);
                    authDispatch({ type: 'LOGOUT' });
                    return Promise.reject(refreshError);
                }
            }
            return Promise.reject(error); // For all other errors, return the error as is.
        }
    );

    //#region Subjects
    const loadSubjects = async () => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetSubjects`, {
                params: { username: account.username }
            });
            return response.data;
        } catch (error) {
            console.log(error)
            return [];
        }
    }

    const updateSubjects = async (updateExistingSubjects) => {
        try {
            const response = await axios.post(`${clientApi}api/training/UpdateExistingSubjects`, updateExistingSubjects, {
                headers: { Authorization: `Bearer ${user.token}` },
                params: { username: account.username, employeeid: account.employeeId }
            });
            return response.data;
        } catch (error) {
            return false;
        }
    }

    const insertSubjects = async (insertSubjects) => {
        try {
            const response = await axios.post(`${clientApi}api/training/InsertSubjects`, insertSubjects, {
                headers: { Authorization: `Bearer ${user.token}` },
                params: { username: account.username, employeeid: account.employeeId }
            });
            return response.data;
        } catch (error) {
            return false;
        }
    }

    const deleteSubjects = async (subjects) => {
        try {
            const response = await axios.post(`${clientApi}api/training/DeleteSubjects`, subjects, {
                headers: { Authorization: `Bearer ${user.token}` },
                params: { username: account.username, employeeid: account.employeeId }
            });
            return response.data;
        } catch (error) {
            return false;
        }
    }
    //#endregion

    //#region Categories
    const loadCategories = async () => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetCategories`, {
                params: { username: account.username }
            });
            
            return response.data;
        } catch (error) {
            console.log(error)
            return [];
        }
    }

    const updateCategories = async (updateExistingCategories) => {
        try {
            const response = await axios.post(`${clientApi}api/training/UpdateExistingCategories`, updateExistingCategories, {
                headers: { Authorization: `Bearer ${user.token}` },
                params: { username: account.username, employeeid: account.employeeId }
            });
            return response.data;
        } catch (error) {
            return false;
        }
    }

    const insertCategories = async (insertCategories) => {
        try {
            const response = await axios.post(`${clientApi}api/training/InsertCategories`, insertCategories, {
                headers: { Authorization: `Bearer ${user.token}` },
                params: { username: account.username, employeeid: account.employeeId }
            });
            return response.data;
        } catch (error) {
            return false;
        }
    }

    const deleteCategories = async (Categories) => {
        try {
            const response = await axios.post(`${clientApi}api/training/DeleteCategories`, Categories, {
                headers: { Authorization: `Bearer ${user.token}` },
                params: { username: account.username, employeeid: account.employeeId }
            });
            return response.data;
        } catch (error) {
            return false;
        }
    }
    //#endregion

    //#region Trainings
    const loadTrainings = async () => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetTrainings`, {
                params: { username: account.username }
            });
            
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const loadTrainingDetails = async (trnUI) => {
        try {
            
            const response = await axiosInstance.get(`${clientApi}api/training/GetTrainingDetails`, {
                params: { username: account.username, trnUI: trnUI }
            });
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const saveTrainingData = async (trainingData) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SaveTraining`, trainingData, {
                params: { username: account.username }
            });
            
            return response.data;
        } catch (error) {
            console.error(error);
            return [];
        }
    };
    //#endregion

    //#region Participants
    const loadParticipants = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetParticipants`, {
                params: { username: account.username, trnUI: trnUI }
            });
            
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const getEmployeeId = async () => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/Employee/GetEmployeeIdByUsername`, {
                params: { username: account.username }
            });
            
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const totalParticipants = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetTotalParticipants`, {
                params: { username: account.username, trnUI: trnUI }
            });
            
            //console.log(response.data)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const saveParticipantsData = async (participants) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SaveParticipants`, participants, {
                params: { username: account.username }
            });
            
            return response.data;
        } catch (error) {
            //console.error('Error saving training data:', error);
            return [];
        }
    };

    const deleteParticipantsData = async (rowguids) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/DeleteParticipants`, rowguids, {
                params: { username: account.username }
            });
            return response.data;
        } catch (error) {
            return false;
        }
    };

    const deleteParticipantsFromTRNUI = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/DeleteParticipantsFromTRNUI`, {
                params: { username: account.username, trnUI: trnUI }
            });
            return response.data;
        } catch (error) {
            return false;
        }
    };

    const checkAuthParticipants = async (fromUI) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/CheckAuthToParticipants`, null, {
                params: { username: account.username, fromUI: fromUI }
            });
            
            return response.data;
        } catch (error) {
            //console.error('Error saving training data:', error);
            return [];
        }
    };

    const copyParticipants = async (fromUI, toUI) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/CopyParticipants`, null, {
                params: { username: account.username, fromUI: fromUI, toUI: toUI }
            });
            
            return response.data;
        } catch (error) {
            //console.error('Error saving training data:', error);
            return [];
        }
    };

    const printParticipants = async (trnUI) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/PrintParticipants`, null, {
                params: { username: username, trnUI: trnUI }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };
    //#endregion

    //#region Modules
    const loadModules = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetModules`, {
                params: { username: account.username, trnUI: trnUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const saveModule = async (mod) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SaveModules`, mod, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };

    const deleteModules = async (modUIs) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/DeleteModules`, modUIs, {
                params: { username: username }
            })
            
            //console.log(response);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };

    const copyModules = async (fromUI, toUI) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/CopyModules`, null, {
                params: { username: account.username, fromTRN: fromUI, toTRN: toUI }
            });
            
            return response.data;
        } catch (error) {
            //console.error(error);
            return [];
        }
    };
    //#endregion

    //#region Attendance
    const loadParticipantsLeft = async (trnUI, moduleUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetParticipantsLeft`, {
                params: { username: account.username, trnUI: trnUI, moduleUI: moduleUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const loadAttendees = async (trnUI, moduleUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetAttendees`, {
                params: { username: account.username, trnUI: trnUI, moduleUI: moduleUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const saveAttendees = async (attendees) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SaveAttendees`, attendees, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };

    const removeAttendees = async (attendees) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/RemoveAttendees`, attendees, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };
    //#endregion

    //#region Facilitators
    const loadFacilitators = async (moduleUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetModuleFacilitators`, {
                params: { username: account.username, moduleUI: moduleUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const saveFacilitators = async (facilitators) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SaveFacilitators`, facilitators, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };

    const deleteFacilitators = async (deleteUIs) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/DeleteFacilitators`, deleteUIs, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };
    //#endregion

    //#region Helpers
    const loadTrainingHelpers = async () => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetTrainingHelper`, {
                params: { username: account.username }
            });
            
            //console.log(response)
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }
    //#endregion

    //#region Prerequisites
    const loadPrerequisites = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetPrerequisites`, {
                params: { username: account.username, trnUI: trnUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const SavePrerequisites = async (prerequisites, trnUI) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SavePrerequisites`, prerequisites, {
                params: { username: username, parentTRNUI: trnUI }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };

    const deletePrerequisites = async (deleteUIs) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/DeletePrerequisites`, deleteUIs, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };

    const loadValidationFlags = async (prerequisites) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/GetPrerequisitesValidation`, prerequisites, {
                params: { username: account.username }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }
    //#endregion

    //#region EvaluationForm
    const loadEvaluationForms = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetEvaluationForm`, {
                params: { username: account.username, trnUI: trnUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const SaveEvaluationForm = async (evaluations) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SaveEvaluationForms`, evaluations, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };

    const DeleteEvaluationForm = async (deletedUIs) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/DeleteEvaluationForms`, deletedUIs, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };
    //#endregion

    //#region TrainingMaterial
    const loadTrainingMaterial = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetTrainingMaterial`, {
                params: { username: account.username, trnUI: trnUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const SaveTrainingMaterial = async (trnMaterial) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SaveTrainingMaterial`, trnMaterial, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            console.log(error);
            return [];
        } finally {
            
        }
    };

    const DeleteTrainingMaterial = async (deletedUIs) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/DeleteTrainingMaterial`, deletedUIs, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };
    //#endregion

    //#region Event
    const loadEvent = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetTRNEvent`, {
                params: { username: account.username, trnUI: trnUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const loadEventOrganizers = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetTRNEventOrganizers`, {
                params: { username: account.username, trnUI: trnUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const saveTRNEvent = async (eventPayload) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/InsertTRNEvent`, eventPayload, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            console.log(error);
            return [];
        } finally {
            
        }
    };

    const saveEventOrganizers = async (organizersData) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/InsertTRNEventOrganizers`, organizersData, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            console.log(error);
            return [];
        } finally {
            
        }
    };
    //#endregion

    //#region LAndDNeed
    const loadLAndDNeed = async () => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetLAndDNeed`, {
                params: { username: account.username }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const saveLAndDNeed = async (payload) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SaveLAndDNeed`, payload, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };

    const deleteLAndDNeed = async (deletedUI) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/DeleteLAndDNeed`, deletedUI, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };
    //#endregion

    //#region LAndDMethod
    const loadLAndDMethod = async () => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetLAndDMethod`, {
                params: { username: account.username }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const saveLAndDMethod = async (payload) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/SaveLAndDMethod`, payload, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };

    const deleteLAndDMethod = async (deletedUI) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/DeleteLAndDMethod`, deletedUI, {
                params: { username: username }
            })
            
            //console.log(response.data);
            return response.data;
        } catch (error) {
            //console.log(error);
            return [];
        } finally {
            
        }
    };
    //#endregion

    //#region MyTrainings
    const loadUserTrainings = async (userid) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetUserTrainings`, {
                params: { username: account.username, userid: userid }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const loadTrainingFromUI = async (trnUI) => {
        try {
            const response = await axiosInstance.get(`${clientApi}api/training/GetTrainingFromUI`, {
                params: { username: account.username, trnUI: trnUI }
            });
            
            return response.data;
        } catch (error) {
            //console.log(error)
            return [];
        }
    }

    const insertUserTrainingData = async (trainingData) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/InsertUserTraining`, trainingData, {
                params: { username: account.username }
            });
            
            return response.data;
        } catch (error) {
            //console.error(error);
            return [];
        }
    };

    const updateUserTrainingData = async (trainingData) => {
        try {
            const response = await axiosInstance.post(`${clientApi}api/training/UpdateUserTraining`, trainingData, {
                params: { username: account.username }
            });
            
            return response.data;
        } catch (error) {
            //console.error(error);
            return [];
        }
    };
    //#endregion

    return {
        loadCategories, updateCategories, insertCategories, deleteCategories,
        loadSubjects, updateSubjects, insertSubjects, deleteSubjects,
        loadTrainings, loadTrainingDetails, loadTrainingHelpers, saveTrainingData,
        loadParticipants, getEmployeeId, totalParticipants, saveParticipantsData,
        deleteParticipantsData, checkAuthParticipants, copyParticipants, printParticipants,
        loadModules, loadParticipantsLeft, loadAttendees, saveAttendees, removeAttendees,
        saveModule, deleteModules, copyModules, loadFacilitators, saveFacilitators,
        deleteFacilitators, loadPrerequisites, SavePrerequisites, deletePrerequisites,
        loadValidationFlags, loadEvaluationForms, SaveEvaluationForm, DeleteEvaluationForm,
        loadTrainingMaterial, SaveTrainingMaterial, DeleteTrainingMaterial, loadEvent,
        loadEventOrganizers, saveTRNEvent, saveEventOrganizers, deleteParticipantsFromTRNUI,
        loadLAndDNeed, saveLAndDNeed, deleteLAndDNeed, loadLAndDMethod, saveLAndDMethod,
        deleteLAndDMethod, loadUserTrainings, loadTrainingFromUI, insertUserTrainingData,
        updateUserTrainingData
    }

}